import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  AlertCircle,
  FileText,
  Clock,
  CheckCircle,
  CheckCircle2,
  Edit2,
  Save,
  X,
  Loader2,
  Tag,
  Hash,
  Sparkles,
  Layers,
  Award,
  Shield,
  ShieldAlert,
  Plus,
  Trash2,
  Cpu,
  Calendar,
  BookOpen,
  Check,
  Search,
} from "lucide-react";
import { knowledgeAPI } from "../api";
import { useAuthStore } from "../store/authStore";
import toast from "react-hot-toast";
import clsx from "clsx";

const statusConfig = {
  approved: {
    label: "Approved",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
  },
  pending_review: {
    label: "Pending Review",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
  },
  draft: {
    label: "Draft",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
  },
  rejected: {
    label: "Rejected",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
  },
};

const categoryOptions = {
  Manual: { label: "Manual", dot: "bg-blue-500" },
  Safety: { label: "Safety", dot: "bg-rose-500" },
  Maintenance: { label: "Maintenance", dot: "bg-amber-500" },
  Operation: { label: "Operation", dot: "bg-emerald-500" },
  Troubleshooting: { label: "Troubleshooting", dot: "bg-purple-500" },
  Specification: { label: "Specification", dot: "bg-cyan-500" },
};

const getCategoryConfig = (cat) => {
  if (!cat) return { label: "General", dot: "bg-blue-500" };
  const match = Object.keys(categoryOptions).find(
    (k) => k.toLowerCase() === cat.toLowerCase()
  );
  return match ? categoryOptions[match] : { label: cat, dot: "bg-blue-500" };
};

const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return null;
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const KnowledgeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [isEditing, setIsEditing] = useState(false);
  const [editedSections, setEditedSections] = useState([]);
  const [editedMetadata, setEditedMetadata] = useState({
    category: "",
    tags: [],
    description: "",
  });
  const [newTagInput, setNewTagInput] = useState("");

  const {
    data: document,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["knowledge-document", id],
    queryFn: () => knowledgeAPI.getById(id).then((res) => res.data.data),
  });

  const userRole = (user?.role || user?.clientRole || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
  const isAdmin = [
    "super_admin",
    "admin",
    "ai_admin",
    "plant_manager",
    "quality_officer",
  ].includes(userRole);
  const docStatus = document?.status?.toLowerCase() || "draft";
  const currentStatus = statusConfig[docStatus] || statusConfig.draft;

  useEffect(() => {
    if (document) {
      setEditedSections(document.content?.sections || []);
      setEditedMetadata({
        category: document.category || "",
        tags: document.tags || [],
        description: document.description || "",
      });
    }
  }, [document]);

  const saveMutation = useMutation({
    mutationFn: (data) => knowledgeAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge-document", id] });
      queryClient.invalidateQueries({ queryKey: ["knowledge"] });
      toast.success("Changes saved successfully");
    },
    onError: (err) =>
      toast.error(err.response?.data?.error || "Failed to save changes"),
  });

  const approveMutation = useMutation({
    mutationFn: (status) => knowledgeAPI.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge-document", id] });
      queryClient.invalidateQueries({ queryKey: ["knowledge"] });
      toast.success("Document approved and published successfully");
      setIsEditing(false);
    },
    onError: (err) =>
      toast.error(err.response?.data?.error || "Failed to update status"),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-3">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        <p className="text-xs sm:text-sm text-slate-500 font-medium">
          Loading document details...
        </p>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-12 text-center max-w-lg mx-auto">
        <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-3">
          <AlertCircle className="w-6 h-6" />
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-1">
          Document Not Found
        </h3>
        <p className="text-xs text-slate-500 mb-5">
          The document you are looking for does not exist or could not be loaded.
        </p>
        <Link to="/knowledge" className="btn btn-secondary text-xs px-4 py-2">
          Back to Knowledge Base
        </Link>
      </div>
    );
  }

  const sectionsToDisplay = isEditing
    ? editedSections
    : document.content?.sections || [];

  const handleSectionChange = (index, field, value) => {
    const updated = [...editedSections];
    updated[index] = { ...updated[index], [field]: value };
    setEditedSections(updated);
  };

  const handleAddSection = () => {
    setEditedSections([
      ...editedSections,
      {
        title: `Section ${editedSections.length + 1}`,
        content: "",
      },
    ]);
  };

  const handleRemoveSection = (indexToRemove) => {
    setEditedSections(editedSections.filter((_, idx) => idx !== indexToRemove));
  };

  const onSaveDraft = () => {
    saveMutation.mutate({
      content: {
        ...(document.content || {}),
        sections: editedSections,
      },
      category: editedMetadata.category,
      tags: editedMetadata.tags,
      description: editedMetadata.description,
    });
  };

  const onApproveAndPublish = () => {
    saveMutation.mutate(
      {
        content: {
          ...(document.content || {}),
          sections: editedSections,
        },
        category: editedMetadata.category,
        tags: editedMetadata.tags,
        description: editedMetadata.description,
      },
      {
        onSuccess: () => approveMutation.mutate("approved"),
      }
    );
  };

  const handleTagAdd = (e) => {
    if (e.key === "Enter" && newTagInput.trim()) {
      e.preventDefault();
      const newTag = newTagInput.trim().toLowerCase();
      if (!editedMetadata.tags.includes(newTag)) {
        setEditedMetadata((prev) => ({
          ...prev,
          tags: [...prev.tags, newTag],
        }));
      }
      setNewTagInput("");
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setEditedMetadata((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedSections(document.content?.sections || []);
    setEditedMetadata({
      category: document.category || "",
      tags: document.tags || [],
      description: document.description || "",
    });
  };

  const confidenceScore = document.extraction?.confidenceScore !== undefined
    ? Math.round(document.extraction.confidenceScore * 100)
    : null;

  const safetyWarnings = (
    Array.isArray(document.content?.safetyWarnings)
      ? document.content.safetyWarnings
      : typeof document.content?.safetyWarnings === "string"
      ? [document.content.safetyWarnings]
      : []
  ).filter((w) => typeof w === "string" && w.trim().length > 0);

  return (
    <div className="space-y-5 lg:space-y-6 w-full">
      {/* Top Header matching Dashboard, Machines & SOPs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-slate-200/80">
        <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
          <Link
            to="/knowledge"
            className="p-2 sm:p-2.5 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200/90 rounded-xl shadow-xs transition-colors shrink-0"
            title="Back to Knowledge Base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight truncate">
                {document.title}
              </h1>
              {document.extraction && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200/80">
                  <Sparkles className="w-3 h-3 text-indigo-600" />
                  AI Extracted
                </span>
              )}
              <span
                className={clsx(
                  "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                  currentStatus.badge
                )}
              >
                <span className={clsx("w-1.5 h-1.5 rounded-full", currentStatus.dot)} />
                <span>{currentStatus.label}</span>
              </span>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500 mt-1 truncate">
              <span className="flex items-center gap-1 font-medium text-slate-600 capitalize">
                <FileText className="w-3.5 h-3.5 text-slate-400" />
                {document.documentType || "Technical Document"}
              </span>
              <span className="text-slate-300">•</span>
              <span>{document.category || "General"}</span>
              {document.version && (
                <>
                  <span className="text-slate-300">•</span>
                  <span>v{document.version}</span>
                </>
              )}
              {document.createdAt && (
                <>
                  <span className="text-slate-300">•</span>
                  <span>
                    Uploaded {new Date(document.createdAt).toLocaleDateString()}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Review Notice Banner */}
      {isAdmin && docStatus !== "approved" && (
        <div
          className={clsx(
            "p-4 sm:p-5 rounded-2xl border flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs",
            isEditing
              ? "bg-blue-50/70 border-blue-200/90 text-blue-900"
              : "bg-amber-50/70 border-amber-200/90 text-amber-900"
          )}
        >
          <div className="flex items-center gap-3.5">
            <div
              className={clsx(
                "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border",
                isEditing
                  ? "bg-blue-100/80 text-blue-600 border-blue-200"
                  : "bg-amber-100/80 text-amber-600 border-amber-200"
              )}
            >
              {isEditing ? (
                <Edit2 className="w-5 h-5" />
              ) : (
                <Clock className="w-5 h-5" />
              )}
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900">
                {isEditing
                  ? "Editing & Reviewing Document Content"
                  : "Pending Technical Review & Verification"}
              </h3>
              <p className="text-xs text-slate-600 mt-0.5">
                {isEditing
                  ? "Make corrections to sections, categorization, or tags. Click 'Save Draft' to persist updates or 'Approve & Publish' to make available system-wide."
                  : "Verify that AI-extracted information, sections, and tags are accurate before publishing to the active knowledge base."}
              </p>
            </div>
          </div>

          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 inline-flex items-center gap-1.5 shrink-0 self-start sm:self-auto"
            >
              <Edit2 className="w-3.5 h-3.5" />
              <span>Start Review</span>
            </button>
          )}
        </div>
      )}

      {/* Main 12-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-start">
        {/* Left Column (8 cols): Overview, Sections, Warnings */}
        <div className="lg:col-span-8 space-y-5 lg:space-y-6">
          {/* Combined Document Scope & Content Sections Card */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 sm:p-6 space-y-5">
            {/* Header: Title & Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Document Scope & Content Sections
                  </h3>
                  <p className="text-xs text-slate-500">
                    Overview and {sectionsToDisplay.length} structured technical sections
                  </p>
                </div>
              </div>

              {/* Action Buttons: Edit Document, Cancel, Save Draft, Approve & Publish */}
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                {isAdmin && !isEditing && (
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="btn btn-secondary text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-3.5 hover:border-slate-400 inline-flex items-center gap-1.5 shadow-2xs font-medium"
                  >
                    <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                    <span>Edit Document</span>
                  </button>
                )}

                {isAdmin && !isEditing && docStatus !== "approved" && (
                  <button
                    type="button"
                    onClick={() => approveMutation.mutate("approved")}
                    disabled={approveMutation.isPending}
                    className="btn bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-3.5 shadow-sm shadow-emerald-600/20 inline-flex items-center gap-1.5 font-medium"
                  >
                    {approveMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CheckCircle className="w-3.5 h-3.5" />
                    )}
                    <span>Approve & Publish</span>
                  </button>
                )}

                {isEditing && (
                  <>
                    <button
                      type="button"
                      onClick={handleCancelEditing}
                      className="btn btn-secondary text-xs sm:text-sm py-1.5 sm:py-2 px-3 hover:border-slate-400 inline-flex items-center gap-1.5"
                    >
                      <X className="w-3.5 h-3.5 text-slate-500" />
                      <span>Cancel</span>
                    </button>

                    <button
                      type="button"
                      onClick={onSaveDraft}
                      disabled={saveMutation.isPending}
                      className="btn btn-secondary text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-3.5 hover:border-slate-300 inline-flex items-center gap-1.5"
                    >
                      {saveMutation.isPending ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Save className="w-3.5 h-3.5" />
                      )}
                      <span>Save Draft</span>
                    </button>

                    <button
                      type="button"
                      onClick={onApproveAndPublish}
                      disabled={approveMutation.isPending || saveMutation.isPending}
                      className="btn bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-3.5 shadow-sm shadow-emerald-600/20 inline-flex items-center gap-1.5 font-medium"
                    >
                      {approveMutation.isPending || saveMutation.isPending ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <CheckCircle className="w-3.5 h-3.5" />
                      )}
                      <span>Approve & Publish</span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Scope & Overview Part */}
            <div className="space-y-1.5">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">
                Scope & Overview
              </span>
              {isEditing ? (
                <textarea
                  value={editedMetadata.description}
                  onChange={(e) =>
                    setEditedMetadata({
                      ...editedMetadata,
                      description: e.target.value,
                    })
                  }
                  placeholder="Provide an overview or summary of this technical document..."
                  className="w-full px-4 py-3 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-2xs transition-all min-h-[90px] resize-y"
                />
              ) : (
                <div className="text-xs sm:text-sm text-slate-700 leading-relaxed whitespace-pre-line bg-slate-50/50 p-3.5 rounded-xl border border-slate-100">
                  {document.description || (
                    <span className="text-slate-400 italic">
                      No overview summary provided for this technical document.
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Content Sections Part */}
            <div className="space-y-3.5 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <Layers className="w-3.5 h-3.5" />
                  </div>
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900">
                    Content Sections
                  </h4>
                  <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                    {sectionsToDisplay.length}
                  </span>
                </div>

                {isEditing && (
                  <button
                    type="button"
                    onClick={handleAddSection}
                    className="btn btn-secondary text-xs py-1 px-2.5 inline-flex items-center gap-1 rounded-lg shadow-2xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Section</span>
                  </button>
                )}
              </div>

            {sectionsToDisplay.length === 0 ? (
              <div className="py-8 px-4 text-center rounded-xl bg-slate-50/60 border border-dashed border-slate-200">
                <BookOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-500 font-medium">
                  No sections found in this document.
                </p>
                {isEditing && (
                  <button
                    type="button"
                    onClick={handleAddSection}
                    className="btn btn-primary text-xs py-1.5 px-3 mt-3 inline-flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Create First Section</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3.5">
                {sectionsToDisplay.map((section, idx) => (
                  <div
                    key={idx}
                    className="p-4 sm:p-5 bg-slate-50/70 border border-slate-200/90 rounded-2xl space-y-3 transition-all hover:bg-slate-50"
                  >
                    {isEditing ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-600 text-white shadow-xs">
                            Section {idx + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveSection(idx)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                            title="Remove section"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="space-y-1">
                          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                            Section Title
                          </label>
                          <input
                            type="text"
                            value={section.title || ""}
                            onChange={(e) =>
                              handleSectionChange(idx, "title", e.target.value)
                            }
                            placeholder="e.g. Emergency Stop Protocol"
                            className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                            Section Content
                          </label>
                          <textarea
                            value={section.content || ""}
                            onChange={(e) =>
                              handleSectionChange(idx, "content", e.target.value)
                            }
                            placeholder="Detailed technical instructions or notes..."
                            className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 min-h-[120px] resize-y"
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-600 text-white shadow-xs">
                              Section {idx + 1}
                            </span>
                            {section.type && (
                              <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-slate-200/70 text-slate-700">
                                {section.type}
                              </span>
                            )}
                          </div>
                        </div>

                        <h4 className="text-sm sm:text-base font-bold text-slate-900">
                          {section.title || `Section ${idx + 1}`}
                        </h4>

                        <div className="text-xs sm:text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {section.content || (
                            <span className="text-slate-400 italic">
                              No body content for this section.
                            </span>
                          )}
                        </div>

                        {/* Subsections if available */}
                        {Array.isArray(section.subsections) &&
                          section.subsections.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-200/80 space-y-2">
                              {section.subsections.map((sub, sIdx) => (
                                <div
                                  key={sIdx}
                                  className="p-3 bg-white border border-slate-200/70 rounded-xl space-y-1"
                                >
                                  <h5 className="text-xs font-bold text-slate-800">
                                    {sub.title || `Subsection ${sIdx + 1}`}
                                  </h5>
                                  <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">
                                    {sub.content}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>

          {/* Safety Warnings & Hazards Card */}
          {safetyWarnings.length > 0 && (
            <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 space-y-3.5">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                <div className="w-7 h-7 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
                  <ShieldAlert className="w-4 h-4" />
                </div>
                <h3 className="text-xs sm:text-sm font-bold text-slate-900">
                  Safety Warnings & Precautions
                </h3>
              </div>

              <div className="space-y-2">
                {safetyWarnings.map((w, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2.5 p-2.5 rounded-xl bg-rose-50/70 border border-rose-200/70 text-xs text-rose-900"
                  >
                    <Shield className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <span className="font-medium leading-relaxed">{w}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column (4 cols): Specs, AI Intelligence, Tags, Source */}
        <div className="lg:col-span-4 space-y-5 lg:space-y-6">
          {/* Quick Specs / Metadata Card */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Award className="w-4 h-4" />
                </div>
                <h3 className="text-xs sm:text-sm font-bold text-slate-900">
                  Document Specs
                </h3>
              </div>

              <div className="flex items-center gap-2">
                <span
                  className={clsx(
                    "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                    currentStatus.badge
                  )}
                >
                  <span
                    className={clsx("w-1.5 h-1.5 rounded-full", currentStatus.dot)}
                  />
                  <span>{currentStatus.label}</span>
                </span>
              </div>
            </div>

            {/* Metadata Rows matching SOPViewer */}
            <div className="divide-y divide-slate-100 text-xs">
              {/* Category Row */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <Layers className="w-3.5 h-3.5 text-slate-400" /> Category
                </span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedMetadata.category}
                    onChange={(e) =>
                      setEditedMetadata({
                        ...editedMetadata,
                        category: e.target.value,
                      })
                    }
                    placeholder="e.g. Maintenance"
                    className="w-36 px-2.5 py-1 text-right font-medium text-xs text-slate-800 bg-slate-50/70 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-2xs"
                  />
                ) : (
                  <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                    <span
                      className={clsx(
                        "w-2 h-2 rounded-full",
                        getCategoryConfig(document.category).dot
                      )}
                    />
                    <span>{document.category || "Uncategorized"}</span>
                  </span>
                )}
              </div>

              {/* Document Type */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <FileText className="w-3.5 h-3.5 text-slate-400" /> Document Type
                </span>
                <span className="font-semibold text-slate-800 capitalize">
                  {document.documentType || "Technical Manual"}
                </span>
              </div>

              {/* Version Row */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <Award className="w-3.5 h-3.5 text-slate-400" /> Version
                </span>
                <span className="font-semibold text-slate-800 font-mono">
                  v{document.version || "1.0"}
                </span>
              </div>

              {/* Language Row */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <BookOpen className="w-3.5 h-3.5 text-slate-400" /> Language
                </span>
                <span className="font-semibold text-slate-800 uppercase">
                  {document.language || "EN"}
                </span>
              </div>

              {/* Created Date */}
              {document.createdAt && (
                <div className="flex justify-between items-center py-2.5 gap-2">
                  <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" /> Upload Date
                  </span>
                  <span className="font-semibold text-slate-800">
                    {new Date(document.createdAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* AI Intelligence & Verification Card */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <h3 className="text-xs sm:text-sm font-bold text-slate-900">
                AI Intelligence & Vector Index
              </h3>
            </div>

            <div className="space-y-3">
              {/* Confidence Meter */}
              <div className="p-3.5 rounded-xl bg-slate-50/80 border border-slate-200/80 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-600 font-medium">
                    Extraction Confidence
                  </span>
                  <span
                    className={clsx(
                      "font-bold text-sm",
                      confidenceScore !== null && confidenceScore >= 80
                        ? "text-emerald-600"
                        : confidenceScore !== null && confidenceScore >= 60
                        ? "text-amber-600"
                        : "text-rose-600"
                    )}
                  >
                    {confidenceScore !== null ? `${confidenceScore}%` : "Analyzed"}
                  </span>
                </div>
                {confidenceScore !== null && (
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div
                      className={clsx(
                        "h-full rounded-full transition-all duration-500",
                        confidenceScore >= 80
                          ? "bg-emerald-500"
                          : confidenceScore >= 60
                          ? "bg-amber-500"
                          : "bg-rose-500"
                      )}
                      style={{ width: `${confidenceScore}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Vector Status Badge */}
              <div className="flex items-center gap-2.5 p-3 rounded-xl bg-emerald-50/70 border border-emerald-200/80 text-xs text-emerald-800">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="font-medium">
                  Indexed in Vector Knowledge Base for AI Retrieval
                </span>
              </div>

              {/* Extraction Pipeline Details */}
              {document.extraction?.method && (
                <div className="flex items-center justify-between text-xs pt-1 px-1">
                  <span className="text-slate-500">Pipeline</span>
                  <span className="font-semibold text-slate-700 capitalize">
                    {document.extraction.method.replace(/_/g, " ")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Tags & Taxonomies Card */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 space-y-3.5">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <Tag className="w-4 h-4" />
                </div>
                <h3 className="text-xs sm:text-sm font-bold text-slate-900">
                  Tags & Indexing
                </h3>
              </div>
              <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-lg">
                {(isEditing ? editedMetadata.tags : document.tags)?.length || 0} Tags
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {(isEditing ? editedMetadata.tags : document.tags)?.map((tag, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200/80 transition-colors"
                >
                  <Tag className="w-3 h-3 text-slate-400" />
                  <span>{tag}</span>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => handleTagRemove(tag)}
                      className="ml-0.5 text-slate-400 hover:text-rose-600 transition-colors"
                      title="Remove tag"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}

              {isEditing && (
                <input
                  type="text"
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  onKeyDown={handleTagAdd}
                  placeholder="+ Add tag (Enter)"
                  className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-32 shadow-2xs"
                />
              )}

              {(!document.tags || document.tags.length === 0) && !isEditing && (
                <p className="text-xs text-slate-400 italic py-1">
                  No tags added to this document.
                </p>
              )}
            </div>
          </div>

          {/* Original Source Document Card */}
          {document.originalFileName && (
            <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 space-y-3.5">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                  <FileText className="w-4 h-4" />
                </div>
                <h3 className="text-xs sm:text-sm font-bold text-slate-900">
                  Source File
                </h3>
              </div>

              <div className="p-3 bg-slate-50/80 border border-slate-200/80 rounded-xl space-y-1">
                <p className="text-xs font-bold text-slate-800 truncate" title={document.originalFileName}>
                  {document.originalFileName}
                </p>
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  {document.fileSize && (
                    <span>{formatFileSize(document.fileSize)}</span>
                  )}
                  {document.originalFileType && (
                    <>
                      <span>•</span>
                      <span className="uppercase">{document.originalFileType}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeDetail;
